import React, {useEffect, useMemo, useState} from 'react'
import {Node} from 'reactflow'
import {toAbsoluteUrl} from '../../../../../../../_metronic/helpers'
import {useReactFlow} from 'reactflow'
import {DelayTime, DelayUnit, IDelayUnit, MetaDataWithDelay} from '../../FlowTypes'
import {TIMING} from '../../../../../../Utils/Constant/FlowConstants'
import {getCurrentTimeWithTimezone} from '../../../../../../Utils/_helpers/timezone'
import Select from 'react-select'
import {useI18n} from '../../../../../../Utils/_helpers/useI18n'

const units = [
  {value: DelayUnit.DAYS, label: 'Days'},
  {value: DelayUnit.HOURS, label: 'Hours'},
  {value: DelayUnit.MINUTES, label: 'Minutes'},
]

const getSafeDelay = (metaData: MetaDataWithDelay): IDelayUnit => {
  const delay = metaData?.delay
  if (!delay || typeof delay !== 'object') {
    return {
      ...metaData?.delay,
      type: DelayTime.RELATIVE,
      value: 1,
      unit: DelayUnit.MINUTES,
      timeFromBrowser: getCurrentTimeWithTimezone(),
    }
  }
  return {
    ...metaData?.delay,
    type:
      delay.type === DelayTime.ABSOLUTE || delay.type === DelayTime.RELATIVE
        ? delay.type
        : DelayTime.RELATIVE,
    value:
      delay.type === DelayTime.ABSOLUTE
        ? typeof delay.value === 'string'
          ? delay.value
          : ''
        : typeof delay.value === 'number'
        ? delay.value
        : 1,
    unit: delay.unit || DelayUnit.MINUTES,
    timeFromBrowser: getCurrentTimeWithTimezone(),
  }
}

const TimeDelaySidebar: React.FC<{node: Node}> = ({node}) => {
  const {setNodes} = useReactFlow()
  const {t} = useI18n()

  const convertToTimeZone = (dateTimeValue: string, browserTimeZone: string) => {
    const [date, time] = dateTimeValue.split('T')
    if (!date || !time) return ''
    const [year, month, day] = date.split('-').map(Number)
    const [hour, minute] = time.split(':').map(Number)

    const localDateString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(
      2,
      '0'
    )}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`

    const localDate = new Date(
      new Date(localDateString).toLocaleString('en-US', {timeZone: browserTimeZone})
    )

    return localDate.toISOString()
  }

  const [relativeValue, setRelativeValue] = useState<number | null>(0)
  const [absoluteValue, setAbsoluteValue] = useState<string>('')
  const [selectedUnit, setSelectedUnit] = useState<DelayUnit>(DelayUnit.MINUTES)
  const [selectedType, setSelectedType] = useState<DelayTime>(DelayTime.RELATIVE)
  const [inputError, setInputError] = useState<string | null>(null)

  const typeOptions = useMemo(
    () => [
      {value: DelayTime.RELATIVE, label: 'Delay for'},
      {value: DelayTime.ABSOLUTE, label: 'Delay until'},
    ],
    []
  )

  const unitOptions = useMemo(() => units, [])

  useEffect(() => {
    const delay = getSafeDelay(node.data?.metaData)

    setSelectedType(delay.type)
    setSelectedUnit(delay.unit)

    if (delay.type === DelayTime.RELATIVE && typeof delay.value === 'number') {
      setRelativeValue(delay.value)
    } else if (delay.type === DelayTime.ABSOLUTE && typeof delay.value === 'string') {
      setAbsoluteValue(delay.value)
    }
  }, [node.id])

  const handleTypeChange = (opt: {value: DelayTime; label: string} | null) => {
    const newType = (opt?.value || DelayTime.RELATIVE) as DelayTime
    setSelectedType(newType)
    setInputError(null)

    setNodes((nds) =>
      nds.map((n) =>
        n.id === node.id
          ? {
              ...n,
              data: {
                ...n.data,
                metaData: {
                  ...n.data?.metaData,
                  delay: {
                    ...n.data?.metaData?.delay,
                    type: newType,
                    value: newType === DelayTime.RELATIVE ? relativeValue : absoluteValue,
                    unit: newType === DelayTime.RELATIVE ? selectedUnit : undefined,
                    timeFromBrowser: getCurrentTimeWithTimezone(),
                  },
                },
              },
            }
          : n
      )
    )
  }

  const handleRelativeValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value

    if (val === '') {
      setRelativeValue(null)
      setInputError('Please enter a valid number')
      return
    }

    if (isNaN(Number(val)) || !/^[0-9]+$/.test(val)) {
      setInputError('Please enter a valid number')
      return
    }

    const numVal = Number(val)

    if (numVal <= 0) {
      return
    }

    setInputError(null)
    setRelativeValue(numVal)

    setNodes((nds) =>
      nds.map((n) =>
        n.id === node.id
          ? {
              ...n,
              data: {
                ...n.data,
                metaData: {
                  ...n.data?.metaData,
                  delay: {
                    ...n.data?.metaData?.delay,
                    value: numVal,
                    timeFromBrowser: getCurrentTimeWithTimezone(),
                  },
                },
              },
            }
          : n
      )
    )
  }

  const handleUnitChange = (opt: {value: DelayUnit; label: string} | null) => {
    const newUnit = (opt?.value || DelayUnit.MINUTES) as DelayUnit
    setSelectedUnit(newUnit)

    setNodes((nds) =>
      nds.map((n) =>
        n.id === node.id
          ? {
              ...n,
              data: {
                ...n.data,
                metaData: {
                  ...n.data?.metaData,
                  delay: {
                    ...n.data?.metaData?.delay,
                    unit: newUnit,
                    timeFromBrowser: getCurrentTimeWithTimezone(),
                  },
                },
              },
            }
          : n
      )
    )
  }

  const handleAbsoluteValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dateTimeValue = e.target.value
    setAbsoluteValue(dateTimeValue)

    const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    setNodes((nds) =>
      nds.map((n) =>
        n.id === node.id
          ? {
              ...n,
              data: {
                ...n.data,
                metaData: {
                  ...n.data?.metaData,
                  delay: {
                    ...n.data?.metaData?.delay,
                    value: dateTimeValue,
                    timeZone: browserTimeZone,
                    timeZoneTime: convertToTimeZone(dateTimeValue, browserTimeZone),
                    timeFromBrowser: getCurrentTimeWithTimezone(),
                  },
                },
              },
            }
          : n
      )
    )
  }

  const item = TIMING[0]

  return (
    <div className='p-4'>
      <div className='d-flex align-items-center gap-3 mb-4'>
        <div
          className='automation-icon-container'
          style={{
            backgroundColor: `${item.bgColor}`,
          }}
        >
          <img src={toAbsoluteUrl(item.svg || '')} alt={item.icon} className='automation-icon' />
        </div>
        <h3 className='mb-0 fw-semibold'>Time Delay</h3>
        <i
          className='bi bi-info-circle ms-1 info'
          title={t('FLOW.SIDEBAR.TIME_DELAY.MORE_INFO')}
          tabIndex={0}
          aria-label={t('FLOW.SIDEBAR.TIME_DELAY.MORE_INFO')}
        ></i>
      </div>

      <Select
        isSearchable={false}
        className='common-select z-1000 mb-2 mt-2'
        value={typeOptions.find((o) => o.value === selectedType) || typeOptions[0]}
        onChange={(opt) => handleTypeChange(opt as any)}
        options={typeOptions}
        menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
        closeMenuOnScroll={true}
        menuShouldBlockScroll={false}
        styles={{
          control: (base) => ({
            ...base,
            backgroundColor: 'var(--bs-gray-200) !important',
            borderColor: base.borderColor,
            boxShadow: base.boxShadow,
            minHeight: 32,
          }),
          valueContainer: (base) => ({...base, paddingTop: 2, paddingBottom: 2}),
          indicatorsContainer: (base) => ({...base, paddingTop: 2, paddingBottom: 2}),
        }}
      />

      {selectedType === DelayTime.RELATIVE ? (
        <>
          <div className='d-flex gap-2 mt-2'>
            <input
              type='number'
              min={0}
              value={relativeValue || ''}
              onChange={handleRelativeValueChange}
              className='form-control form-control-sm'
              style={{width: '80px'}}
            />
            <Select
              isSearchable={false}
              className='common-select z-1000'
              value={unitOptions.find((o) => o.value === selectedUnit) || unitOptions[2]}
              onChange={(opt) => handleUnitChange(opt as any)}
              options={unitOptions}
              menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
              closeMenuOnScroll={true}
              menuShouldBlockScroll={false}
              styles={{
                control: (base) => ({
                  ...base,
                  backgroundColor: 'var(--bs-gray-200) !important',
                  borderColor: base.borderColor,
                  boxShadow: base.boxShadow,
                  minHeight: 32,
                }),
                valueContainer: (base) => ({...base, paddingTop: 2, paddingBottom: 2}),
                indicatorsContainer: (base) => ({...base, paddingTop: 2, paddingBottom: 2}),
              }}
            />
          </div>
          {inputError && (
            <div style={{color: 'var(--bs-danger)', fontSize: 12, marginTop: 2}}>{inputError}</div>
          )}
        </>
      ) : (
        <input
          type='datetime-local'
          value={absoluteValue}
          onChange={handleAbsoluteValueChange}
          className='form-control form-control-sm mt-2'
        />
      )}
    </div>
  )
}

export default TimeDelaySidebar
